import { doc, getDoc, setDoc, onSnapshot, serverTimestamp, runTransaction } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { DayPerformance, DailySummary, TrackedNutrientKey } from '@/types'
import type { DayTargets } from '@/kidney/dailyTargets'
import type { StoredDayPral } from '@/kidney/pral'
import { daySummaryFromData, type DaySummaryDoc } from '@/lib/firestore/progress'

/** kpal_users/{uid}/dailySummaries/{YYYY-MM-DD} — powers the week-strip dots. */
function summaryRef(uid: string, date: string) {
  return doc(db, 'kpal_users', uid, 'dailySummaries', date)
}

/** Result of reading a day doc: `error` is NOT absence — writers must never backfill on it. */
export type DaySummaryDocState =
  | { status: 'missing' }
  | { status: 'loaded'; doc: DaySummaryDoc }
  | { status: 'error' }

export async function fetchDaySummaryState(uid: string, date: string): Promise<DaySummaryDocState> {
  try {
    const snap = await getDoc(summaryRef(uid, date))
    if (!snap.exists()) return { status: 'missing' }
    return { status: 'loaded', doc: daySummaryFromData(date, snap.data()) }
  } catch (err) {
    console.warn('Daily summary read failed:', err)
    return { status: 'error' }
  }
}

/**
 * Every tracked total, keyed like DayTargets.values — mobile parity.
 * 4.39: iron is the known subtotal at 2 decimals, omitted when no entry has a value.
 */
function trackedTotals(summary: DailySummary): Partial<Record<TrackedNutrientKey, number>> {
  const t = summary.totals
  const r = (v: number | undefined) => Math.round(((v || 0) + Number.EPSILON) * 10) / 10
  return {
    sodium: r(t.sodiumMg), potassium: r(t.potassiumMg), phosphorus: r(t.phosphorusMg),
    protein: r(t.proteinG), fluid: r(t.fluidMl),
    calories: r(t.calories), carbs: r(t.carbsG), fat: r(t.fatG),
    magnesium: r(t.magnesiumMg), calcium: r(t.calciumMg), vitaminK: r(t.vitaminKMcg),
    ...(t.ironMg !== undefined ? { iron: Math.round((t.ironMg + Number.EPSILON) * 100) / 100 } : {}),
    oxalate: r(t.oxalateMg), sugars: r(t.sugarsG), fiber: r(t.fiberG), saturatedFat: r(t.saturatedFatG),
  }
}

/**
 * W2: the day's totals + performance. 4.34 adds `totals` (every tracked
 * nutrient) stamped with `totalsAt` in the SAME write as `updatedAt`, so the
 * two server timestamps resolve equal and readers can tell a fresh map from
 * one an older client left behind.
 */
export async function writeDailySummary(
  uid: string,
  summary: DailySummary,
  performance: DayPerformance,
  totalItems: number,
  pral: StoredDayPral,
): Promise<void> {
  await setDoc(
    summaryRef(uid, summary.date),
    {
      date: summary.date,
      totalItems,
      sodium: Math.round(summary.totals.sodiumMg),
      potassium: Math.round(summary.totals.potassiumMg),
      phosphorus: Math.round(summary.totals.phosphorusMg),
      protein: Math.round(summary.totals.proteinG),
      fluid: Math.round(summary.totals.fluidMl),
      performance,
      totals: trackedTotals(summary),
      totalsAt: serverTimestamp(),
      // 4.35 PRAL: sum + coverage. A 4.34 client rewriting the day moves `updatedAt` and
      // leaves `pralAt` behind — that is what marks the stored value stale (isPralFresh).
      pral,
      pralAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    // 4.39: each written field is REPLACED whole, so a total that dropped out
    // (iron after its last known entry is deleted) can't survive a deep merge
    // next to a fresh `totalsAt`. Unlisted fields (targets, notes) are untouched.
    {
      mergeFields: [
        'date', 'totalItems', 'sodium', 'potassium', 'phosphorus', 'protein', 'fluid',
        'performance', 'totals', 'totalsAt', 'pral', 'pralAt', 'updatedAt',
      ],
    },
  )
}

/**
 * W1: the day's resolved targets (today only). The `targets` map is REPLACED
 * whole via mergeFields — a deep merge would keep a removed bound alive.
 */
export async function writeDailyTargets(uid: string, date: string, targets: DayTargets): Promise<void> {
  await setDoc(
    summaryRef(uid, date),
    { date, targets, targetsAt: serverTimestamp() },
    { mergeFields: ['date', 'targets', 'targetsAt'] },
  )
}

/** W3: backfill a past day's targets only if no snapshot exists — transactional. */
export async function createDailyTargetsIfAbsent(
  uid: string,
  date: string,
  targets: DayTargets,
): Promise<'created' | 'exists'> {
  const ref = summaryRef(uid, date)
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref)
    const existing = snap.exists() ? (snap.data().targets as DayTargets | undefined) : undefined
    if (existing && existing.v === 1) return 'exists'
    tx.set(ref, { date, targets, targetsAt: serverTimestamp() }, { mergeFields: ['date', 'targets', 'targetsAt'] })
    return 'created'
  })
}

/**
 * Daily notes live on the SAME dailySummaries/{date} doc as the nutrient rollup
 * (field `dailyNotes`) — mirrors mobile FirestoreService.updateDailyNotes.
 * Always merge; never clobber the totals. Stamps `notesUpdatedAt` and leaves
 * `updatedAt` alone so a note cannot make the extended totals look stale.
 */
export async function updateDailyNotes(uid: string, date: string, notes: string): Promise<void> {
  await setDoc(
    summaryRef(uid, date),
    { dailyNotes: notes, notesUpdatedAt: serverTimestamp() },
    { merge: true },
  )
}

/** Live dailyNotes for a date (real-time so phone edits appear on web). */
export function subscribeDailyNotes(
  uid: string,
  date: string,
  onUpdate: (notes: string) => void,
): () => void {
  return onSnapshot(summaryRef(uid, date), (snap) => {
    onUpdate(snap.exists() ? ((snap.data().dailyNotes as string) || '') : '')
  })
}

/** Raw day docs for a set of dates (null = no doc). Readers derive the colour themselves (4.34). */
export async function fetchDayDocs(uid: string, days: string[]): Promise<Record<string, DaySummaryDoc | null>> {
  const out: Record<string, DaySummaryDoc | null> = {}
  const snaps = await Promise.all(days.map((d) => getDoc(summaryRef(uid, d)).catch(() => null)))
  snaps.forEach((snap, i) => {
    out[days[i]] = snap?.exists() ? daySummaryFromData(days[i], snap.data()) : null
  })
  return out
}
